import {
  Component,
  Input,
  EventEmitter,
  ChangeDetectionStrategy,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';

@Component({
  selector: 'cpd-stroked-button',
  templateUrl: './stroked-button.component.html',
  styleUrls: ['./stroked-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StrokedButtonComponent implements OnInit, OnChanges {
  @Input() text: string;

  /**
   * Accessible name for icon-only usage. When `text` is empty the button
   * renders as an icon with no content, so without this its accessible name
   * is empty and screen readers announce only "button" (WCAG 4.1.2).
   */
  @Input() ariaLabel?: string;

  @Input() fontSet = 'fas';
  @Input() fontIcon: string;
  @Input() iconSide: 'left' | 'right' = 'left';

  @Input() color: 'basic' | 'primary' | 'secondary' = 'basic';
  @Input() size: 'small' | 'medium' | 'large';
  @Input() disabled = false;

  /**
   * Unavailable, but still reachable and still able to explain itself.
   *
   * `disabled` removes a control from the tab order, which takes its tooltip
   * and its reason with it: a keyboard or screen-reader user meets a button
   * they cannot reach and are told nothing about why. 00-foundations section 6
   * and 02-home section 10 both call for aria-disabled with the reason in the
   * accessible name instead. The click is swallowed here rather than by the
   * browser.
   */
  @Input() ariaDisabled = false;
  @Input() type = 'button';
  @Input() backgroundColor: 'transparent' | 'solid' = 'solid';

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() click = new EventEmitter<Event>();

  ngClass: any;

  ngOnInit() {
    this.calcNgClass();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.text && !changes.text.isFirstChange()) ||
      (changes.color && !changes.color.isFirstChange()) ||
      (changes.size && !changes.size.isFirstChange()) ||
      (changes.disabled && !changes.disabled.isFirstChange()) ||
      (changes.ariaDisabled && !changes.ariaDisabled.isFirstChange())
    ) {
      this.calcNgClass();
    }
  }

  calcNgClass() {
    this.ngClass = {
      disabled: this.disabled || this.ariaDisabled,

      'text-black border-black': this.color === 'basic',
      'hover:bg-black-hover-light':
        this.color === 'basic' && this.backgroundColor === 'solid',
      'hover:bg-black-hover-transparent':
        this.color === 'basic' && this.backgroundColor === 'transparent',

      'text-green border-green': this.color === 'primary',
      'hover:bg-green-hover-light':
        this.color === 'primary' && this.backgroundColor === 'solid',
      'hover:bg-green-hover-transparent':
        this.color === 'primary' && this.backgroundColor === 'transparent',

      'text-blue border-blue': this.color === 'secondary',
      'hover:bg-blue-hover-light':
        this.color === 'secondary' && this.backgroundColor === 'solid',
      'hover:bg-blue-hover-transparent':
        this.color === 'secondary' && this.backgroundColor === 'transparent',

      'bg-white': true,
      border: true,

      responsive: this.size === undefined,
      small: this.size === 'small',
      medium: this.size === 'medium',
      large: this.size === 'large',

      'no-text': !this.text
    };
  }

  onClick(event: Event) {
    event.stopPropagation();

    // ariaDisabled leaves the button focusable and clickable to the browser, so
    // the swallow happens here rather than in the DOM.
    if (this.disabled || this.ariaDisabled) {
      event.preventDefault();
      return;
    }

    if (this.click.observers.length > 0) {
      this.click.emit(event);
    }
  }
}
